import React, { useCallback } from 'react';
import { FileText, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface PDFUploadDropzoneProps {
  onFileSelect: (file: File) => void;
  isLoading?: boolean;
}

const isPdfFile = (file: File): boolean => file.type === 'application/pdf' || file.name.endsWith('.pdf');

export const PDFUploadDropzone: React.FC<PDFUploadDropzoneProps> = ({
  onFileSelect,
  isLoading = false,
}) => {
  const handleSelectedFile = useCallback(
    (file: File | undefined) => {
      if (!file) return;
      if (isPdfFile(file)) {
        onFileSelect(file);
        return;
      }
      alert('Please select a valid PDF file.');
    },
    [onFileSelect]
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleSelectedFile(event.target.files?.[0]);
      event.target.value = '';
    },
    [handleSelectedFile]
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      handleSelectedFile(event.dataTransfer.files?.[0]);
    },
    [handleSelectedFile]
  );

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  }, []);

  return (
    <div
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      className="flex flex-col items-center justify-center w-full h-full min-h-[400px] p-8 border-2 border-dashed border-border rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
    >
      <div className="flex flex-col items-center space-y-4">
        <div className="p-6 bg-primary/10 rounded-full">
          <FileText className="w-12 h-12 text-primary" />
        </div>
        <div className="text-center space-y-2">
          <h3 className="text-xl font-semibold text-foreground">Upload a PDF Document</h3>
          <p className="text-sm text-muted-foreground">Drag and drop your PDF file here, or click to browse</p>
        </div>
        <label htmlFor="pdf-upload-input">
          <Button
            type="button"
            disabled={isLoading}
            className="cursor-pointer"
            onClick={() => document.getElementById('pdf-upload-input')?.click()}
          >
            <Upload className="w-4 h-4 mr-2" />
            {isLoading ? 'Loading...' : 'Select PDF File'}
          </Button>
          <input
            id="pdf-upload-input"
            type="file"
            accept=".pdf,application/pdf"
            onChange={handleFileChange}
            className="hidden"
            disabled={isLoading}
          />
        </label>
        <p className="text-xs text-muted-foreground">Supported format: PDF</p>
      </div>
    </div>
  );
};
